import React, { lazy, Suspense, useState } from 'react';

const ImagePreview = lazy(() =>
	import('../ImagePreviewModal').then((m) => ({ default: m.ImagePreview })),
);

interface MessageImagesProps {
	images: string[];
}

/** Renders the image grid for user messages, with preview-on-click. */
export function MessageImages({ images }: MessageImagesProps) {
	const [previewImage, setPreviewImage] = useState<string | null>(null);

	if (!images || images.length === 0) return null;

	return (
		<>
			<div className="flex flex-col ml-auto mr-6 mt-2">
				<div className="flex flex-row flex-wrap gap-3 justify-end">
					{images.map((image, index) => (
						<div key={index} className="relative group">
							<div className="w-[200px] h-[175px] rounded-md overflow-hidden border border-neutral-700 hover:border-neutral-400 transition-all duration-200 shadow-md relative">
								<img
									className="w-full h-full object-contain bg-black-900 cursor-pointer"
									src={image}
									alt={`User uploaded content ${index + 1}`}
									onClick={() => setPreviewImage(image)}
									loading="lazy"
									onLoad={(e) => {
										const img = e.target as HTMLImageElement;
										const aspectRatio = img.naturalWidth / img.naturalHeight;
										if (aspectRatio > 2.5 || aspectRatio < 0.4) {
											img.classList.remove('object-cover');
											img.classList.add('object-contain');
										} else {
											img.classList.remove('object-contain');
											img.classList.add('object-cover');
										}
									}}
								/>
								<div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200">
									<div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
										<button
											className="bg-neutral-800 bg-opacity-80 p-2 rounded-full"
											onClick={(e) => {
												e.stopPropagation();
												setPreviewImage(image);
											}}
											title="View full image"
											type="button"
											aria-label={`View full image ${index + 1}`}
										>
											<i className="fas fa-search-plus text-neutral-200" />
										</button>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{previewImage && (
				<Suspense fallback={null}>
					<ImagePreview image={previewImage} onClose={() => setPreviewImage(null)} />
				</Suspense>
			)}
		</>
	);
}
